import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Users, TrendingUp, Coins, ChevronRight, X, Phone } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/utils'

// Admin view of the referral programme. Mirrors the customer-side
// /users/me/referral/list state model (signed_up / first_order_placed /
// bonus_credited) so the two surfaces never disagree on what happened
// to a referral. Use this page to spot prolific referrers, see which
// referrals stalled at signup, and audit total drops paid out.

type Referrer = {
  id: string
  full_name: string | null
  phone: string
  referral_code: string | null
  joined_at: string
  total_referees: number
  signed_up: number
  first_order_placed: number
  bonus_credited: number
  total_drops_awarded: number
  conversion_rate: number
  latest_referee_signup_at: string
}

type Summary = {
  total_referrers: number
  total_referees: number
  conversion_rate: number
  total_drops_awarded: number
}

type ListResponse = {
  referrers: Referrer[]
  summary: Summary
}

type RefereeState = 'bonus_credited' | 'first_order_placed' | 'signed_up'

type Referee = {
  id: string
  full_name: string | null
  phone: string
  signed_up_at: string
  first_order_at: string | null
  drops_awarded: number
  status: RefereeState
}

type DetailResponse = {
  referrer: Pick<Referrer, 'id' | 'full_name' | 'phone' | 'referral_code' | 'joined_at'>
  referees: Referee[]
}

const STATE_LABEL: Record<RefereeState, string> = {
  bonus_credited:     'Bonus credited',
  first_order_placed: 'First order placed',
  signed_up:          'Signed up',
}

const STATE_COLOR: Record<RefereeState, string> = {
  bonus_credited:     'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  first_order_placed: 'bg-[#C41E3A]/15 text-[#C41E3A] border-[#C41E3A]/30',
  signed_up:          'bg-gray-500/15 text-gray-400 border-gray-500/30',
}

export default function Referrals() {
  const [opened, setOpened] = useState<Referrer | null>(null)

  const { data, isLoading, error } = useQuery<ListResponse>({
    queryKey: ['admin-referrals'],
    queryFn: () => api.get('/referrals').then(r => r.data),
    refetchInterval: 60_000,
  })

  return (
    <div className="p-6">
      <div className="mb-5">
        <h1 className="text-lg font-semibold text-white">Referrals</h1>
        <p className="text-xs text-gray-500 mt-0.5">
          Who's driving sign-ups, where each referral stalled, and total drops paid out.
          Rates live in <a href="/settings" className="text-[#C41E3A] hover:underline">Settings → Drops Economy</a>.
        </p>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <SummaryTile
          icon={<Users size={14} />}
          label="Referrers"
          value={data?.summary.total_referrers ?? 0}
        />
        <SummaryTile
          icon={<Users size={14} />}
          label="Referees"
          value={data?.summary.total_referees ?? 0}
        />
        <SummaryTile
          icon={<TrendingUp size={14} />}
          label="Conversion"
          value={
            data
              ? `${(data.summary.conversion_rate * 100).toFixed(0)}%`
              : '—'
          }
          sub="of referees placed first order + got bonus credited"
        />
        <SummaryTile
          icon={<Coins size={14} />}
          label="Drops awarded"
          value={(data?.summary.total_drops_awarded ?? 0).toLocaleString()}
        />
      </div>

      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
        {isLoading && (
          <div className="p-6 text-center text-xs text-gray-500">Loading…</div>
        )}
        {error && (
          <div className="p-6 text-center text-xs text-[#C41E3A]">Failed to load referrals.</div>
        )}
        {data && data.referrers.length === 0 && (
          <div className="p-6 text-center text-xs text-gray-500">No referrals yet. Once a customer's code is used, they show up here.</div>
        )}
        {data && data.referrers.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-[#0f0f0f] text-gray-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left  px-4 py-2.5 font-medium">Referrer</th>
                <th className="text-left  px-4 py-2.5 font-medium">Code</th>
                <th className="text-right px-4 py-2.5 font-medium" title="Total accounts who used this code">Total</th>
                <th className="text-right px-4 py-2.5 font-medium" title="Signed up but haven't placed an order yet — candidates for a marketing nudge">Pending</th>
                <th className="text-right px-4 py-2.5 font-medium" title="Cumulative — placed at least one order (delivered or otherwise)">Ordered</th>
                <th className="text-right px-4 py-2.5 font-medium" title="Cumulative subset of Ordered — the referrer bonus was credited">Bonus paid</th>
                <th className="text-right px-4 py-2.5 font-medium">Conv.</th>
                <th className="text-right px-4 py-2.5 font-medium">Drops</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2e2e2e]">
              {data.referrers.map(r => {
                // Cumulative: Ordered counts anyone who placed an order
                // (including those whose bonus already paid). Pending is
                // the signed-up-but-haven't-ordered bucket — useful for
                // marketing nudges.
                const ordered = r.first_order_placed + r.bonus_credited
                return (
                  <tr
                    key={r.id}
                    onClick={() => setOpened(r)}
                    className="hover:bg-[#0f0f0f] cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <div className="text-white">{r.full_name ?? '—'}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5">{r.phone}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-[11px] text-gray-400">{r.referral_code ?? '—'}</td>
                    <td className="px-4 py-3 text-right text-white font-medium">{r.total_referees}</td>
                    <td className="px-4 py-3 text-right text-gray-400">{r.signed_up}</td>
                    <td className="px-4 py-3 text-right text-[#C41E3A]">{ordered}</td>
                    <td className="px-4 py-3 text-right text-emerald-400">{r.bonus_credited}</td>
                    <td className="px-4 py-3 text-right text-gray-400">{(r.conversion_rate * 100).toFixed(0)}%</td>
                    <td className="px-4 py-3 text-right text-gray-400">{r.total_drops_awarded.toLocaleString()}</td>
                    <td className="px-2 text-gray-600"><ChevronRight size={14} /></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {opened && <RefereeDrawer referrer={opened} onClose={() => setOpened(null)} />}
    </div>
  )
}

function SummaryTile({
  icon, label, value, sub,
}: {
  icon: React.ReactNode
  label: string
  value: number | string
  sub?: string
}) {
  return (
    <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl px-4 py-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-semibold text-white tabular-nums">{value}</div>
      {sub && <div className="text-[10px] text-gray-600 mt-1">{sub}</div>}
    </div>
  )
}

function RefereeDrawer({ referrer, onClose }: { referrer: Referrer; onClose: () => void }) {
  const { data } = useQuery<DetailResponse>({
    queryKey: ['admin-referrals', referrer.id],
    queryFn: () => api.get(`/referrals/${referrer.id}`).then(r => r.data),
  })

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex justify-end" onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        className="bg-[#0f0f0f] w-full max-w-md h-full overflow-y-auto border-l border-[#2e2e2e]"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2e2e2e] sticky top-0 bg-[#0f0f0f] z-10">
          <div>
            <h2 className="text-base font-semibold text-white">{referrer.full_name ?? '—'}</h2>
            <div className="flex items-center gap-2 text-[11px] text-gray-500 mt-0.5">
              <Phone size={11} /> {referrer.phone}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 border-b border-[#2e2e2e]">
          <div className="grid grid-cols-3 gap-3 text-center">
            <Stat label="Total" value={referrer.total_referees} tone="white" />
            <Stat label="Credited" value={referrer.bonus_credited} tone="emerald" />
            <Stat label="Drops" value={referrer.total_drops_awarded.toLocaleString()} tone="amber" />
          </div>
          {referrer.referral_code && (
            <div className="mt-3 text-[11px] text-gray-500 text-center">
              Code: <span className="font-mono text-gray-300">{referrer.referral_code}</span>
            </div>
          )}
        </div>

        <div className="px-5 py-4">
          <h3 className="text-[10px] uppercase tracking-wider text-gray-500 mb-3">Referees</h3>
          {!data && <div className="text-xs text-gray-500">Loading…</div>}
          {data && data.referees.length === 0 && (
            <div className="text-xs text-gray-500">No referees yet.</div>
          )}
          <div className="space-y-3">
            {data?.referees.map(r => (
              <div key={r.id} className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2.5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-sm text-white">{r.full_name ?? '—'}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">{r.phone}</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full border text-[10px] font-medium ${STATE_COLOR[r.status]}`}>
                    {STATE_LABEL[r.status]}
                  </span>
                </div>
                <div className="mt-2 text-[11px] text-gray-500 grid grid-cols-2 gap-1">
                  <span>Signed up {formatDateTime(r.signed_up_at)}</span>
                  {r.first_order_at && <span>First order {formatDateTime(r.first_order_at)}</span>}
                  {r.drops_awarded > 0 && (
                    <span className="text-amber-400">+{r.drops_awarded} drops to referrer</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function Stat({ label, value, tone }: { label: string; value: number | string; tone: 'white' | 'emerald' | 'amber' }) {
  const color =
    tone === 'emerald' ? 'text-emerald-400' :
    tone === 'amber' ? 'text-amber-400' :
    'text-white'
  return (
    <div>
      <div className={`text-xl font-semibold tabular-nums ${color}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">{label}</div>
    </div>
  )
}
